// ── Statutory identity (client single point) — tax-ID rule, legal forms, honorifics ───────────────
// Mirror of server/src/services/statutory.ts. The rules arrive on /api/me as `me.statutory`: the country
// pack of the region country (server services/countryPacks.ts — Thailand is the only pack with these
// fields). Nothing here spells a country's titles or legal forms; with no pack (before /api/me, or a
// country without one) only the product's English words apply and a tax ID is only checked for being
// filled in. Keep the two files in step.
import { useStore } from '../store';

export type TaxIdRule = { digits: number; checksum: 'th-mod11'; label: string; juristicPrefix?: string };
export type LegalForms = { en: string; words: string[]; heads: string[]; tail: string; tailQualifier: string; nonCompany: string[]; script: string };
export type Honorifics = { latin: string[]; latinAbbrev: string[]; native: string[]; nativeAbbrev: string[]; nativeNameStart: string; fromEnglish: Record<string, string> };
export type Statutory = { country: string; taxId?: TaxIdRule; legalSuffixes?: LegalForms; honorifics?: Honorifics; amountInWords?: string; vatRate?: number };

const NONE: Statutory = { country: '' };
// A page with no login may hand in the pack its own bootstrap fetched; used only while the store holds none.
let override: Statutory | null = null;
export const setStatutoryOverride = (s: Statutory | null): void => { override = s; };

/** The statutory pack as the store holds it right now (`{ country: '' }` before /api/me). */
export function statutory(): Statutory {
  let s: any;
  try { s = (useStore.getState().me as any)?.statutory; } catch { /* store not ready */ }
  return (s && typeof s === 'object' ? s : null) || override || NONE;
}

// ── Regex helpers (memoised per pack object) ───────────────────────────────────────────────────────
const esc = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const caches = new WeakMap<object, Map<string, RegExp | null>>();
function memo(key: string, build: (s: Statutory) => RegExp | null): RegExp | null {
  const s = statutory();
  let m = caches.get(s);
  if (!m) { m = new Map(); caches.set(s, m); }
  if (!m.has(key)) m.set(key, build(s));
  return m.get(key)!;
}
const longestFirst = (a: string[]): string[] => a.map((w, i) => ({ w, i })).sort((x, y) => y.w.length - x.w.length || x.i - y.i).map(x => x.w);

// ── Tax ID ─────────────────────────────────────────────────────────────────────────────────────────
export type TaxIdReason = '' | 'empty' | 'length' | 'checksum';
const thMod11 = (d: string): boolean => {
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += Number(d[i]) * (13 - i);
  return (11 - (sum % 11)) % 10 === Number(d[12]);
};
const ruleFor = (country?: string | null): TaxIdRule | undefined => {
  const s = statutory();
  return !country || String(country).toUpperCase() === s.country ? s.taxId : undefined;
};
/** The comparable form of a tax ID: digits only where the country's number is all digits, else upper-case letters + digits. */
export const taxIdNormalize = (raw: any, country?: string | null): string => {
  const s = String(raw == null ? '' : raw);
  return ruleFor(country) ? s.replace(/\D/g, '') : s.toUpperCase().replace(/[^0-9A-Z]/g, '');
};
/** THE tax-ID validator (mirror of the server's): the session's country by default; a country whose rule
 *  this browser does not hold is only checked for being filled in. */
export function taxIdCheck(raw: any, country?: string | null): { ok: boolean; reason: TaxIdReason; value: string } {
  const rule = ruleFor(country);
  const value = taxIdNormalize(raw, country);
  if (!value) return { ok: false, reason: 'empty', value };
  if (!rule) return { ok: true, reason: '', value };
  if (value.length !== rule.digits) return { ok: false, reason: 'length', value };
  if (rule.checksum === 'th-mod11' && !thMod11(value)) return { ok: false, reason: 'checksum', value };
  return { ok: true, reason: '', value };
}
/** The number's statutory name ('' when the country has no rule) — shown as-is in every language. */
export const taxIdLabel = (): string => statutory().taxId?.label || '';

// ── Honorifics ─────────────────────────────────────────────────────────────────────────────────────
/** Product constant: the English titles every tenant recognises, in their canonical spelling. */
export const HONORIFICS_EN = ['Mr.', 'Mrs.', 'Ms.', 'Miss', 'Dr.'];
const keyOf = (t: string): string => String(t || '').trim().replace(/\.$/, '').toLowerCase();
export type HonorificOpts = { extra?: string[]; local?: boolean; abbrev?: boolean; native?: boolean; en?: boolean };
export function honorificWords(opts: HonorificOpts = {}): string[] {
  const h = statutory().honorifics;
  const out = [
    ...(opts.en === false ? [] : HONORIFICS_EN.map(keyOf)),
    ...(opts.extra || []).map(keyOf),
    ...(opts.local === false ? [] : (h?.latin || []).map(keyOf)),
    ...(opts.abbrev ? (h?.latinAbbrev || []).map(keyOf) : []),
  ];
  if (opts.native) out.push(...longestFirst(h?.native || []));
  return out.filter((w, i) => w && out.indexOf(w) === i);
}
/** A RegExp around the honorific alternation: `{H}` in `template` is replaced by it (memoised). */
export function honorificRe(template: string, flags = '', opts: HonorificOpts = {}): RegExp {
  return memo(`h|${template}|${flags}|${JSON.stringify(opts)}`, () =>
    new RegExp(template.split('{H}').join(honorificWords(opts).map(esc).join('|')), flags))!;
}
/** Is this single token a title ("Mr.", "mrs", "Khun")? */
export const isHonorificToken = (tok: any): boolean => honorificRe('^({H})\\.?$', 'i').test(String(tok == null ? '' : tok));
/** The title choices a name form offers: the English ones, then the region country's own. */
export const honorificOptions = (): string[] => [...HONORIFICS_EN, ...(statutory().honorifics?.latin || [])];
/** A local-script title at the start of a name, only where a name can begin right after it; null when none. */
export function nativeTitleRe(): RegExp | null {
  return memo('nt', s => {
    const h = s.honorifics;
    if (!h?.native?.length) return null;
    return new RegExp(`^(?:${longestFirst(h.native).map(esc).join('|')})(?:\\s+|(?=[${h.nativeNameStart}]))`);
  });
}

// ── Legal forms ────────────────────────────────────────────────────────────────────────────────────
/** Product constant: a COMPANY string, not a person (English / international forms) — never split into person parts. */
export const CORPORATE_RE = /\b(co\.?,?\s*ltd|ltd\.?|llc|limited|company|corp\.?|inc\.?|kft|gmbh|sp\.? ?z ?o\.? ?o|fundacja|spolka|spó?lka|akcyjna|holdings?|organizacji)\b/i;
/** The local legal form spelled in FRONT of a name (with the space after it); null when none. */
export function legalHeadRe(): RegExp | null {
  return memo('lh', s => {
    const heads = s.legalSuffixes?.heads || [];
    if (!heads.length) return null;
    return new RegExp(`^(?:${heads.map(h => (h.endsWith('.') ? `${esc(h.slice(0, -1))}\\.?` : esc(h))).join('|')})\\s*`);
  });
}
/** The local legal form spelled AFTER a name, with its optional bracketed qualifier; null when none. */
export function legalTailRe(): RegExp | null {
  return memo('lt', s => {
    const l = s.legalSuffixes;
    if (!l?.tail) return null;
    return new RegExp(`\\s*${esc(l.tail)}\\s*${l.tailQualifier ? `(?:\\(\\s*${esc(l.tailQualifier)}\\s*\\))?` : ''}$`);
  });
}
/** A file's own markers after the legal name — "(Building)", "[K4]" — excluding the legal form's bracketed qualifier. */
export function nameMarkersRe(): RegExp {
  return memo('mk', s => {
    const q = s.legalSuffixes?.tailQualifier;
    return new RegExp(`(?:\\s*(?:\\[[^\\]]*\\]|\\(${q ? `(?!\\s*${esc(q)}\\s*\\))` : ''}[^)]*\\)))+$`);
  })!;
}
